import { apiClient } from '@/lib/api-client';
import type { ApiResponse } from '@/types/api';
import type {
  Manifest,
  ManifestDetail,
  ManifestBarcode,
  ScanEvent,
  ReconciliationReport,
  BarcodeImportResult,
  MarkScannedResult,
  AuditLog,
  ManifestStatus,
  CreateManifestRequest,
  UpdateManifestRequest,
  ImportBarcodesRequest,
  AddBarcodeRequest,
} from '@/types/manifest';

// Request type for recording scans
export interface RecordScanRequest {
  barcodeValue: string;
  timestamp: string;
  scanType?: 'Normal' | 'Manual' | 'Forced';
  flag?: 'None' | 'Duplicate' | 'Unmanifested' | 'Manual';
  latitude?: number;
  longitude?: number;
  deviceId?: string;
}

// Manifest CRUD

export async function getManifests(status?: ManifestStatus, name?: string, dateFrom?: string, dateTo?: string): Promise<ApiResponse<Manifest[]>> {
  const params: Record<string, string> = {};
  if (status) params.status = status;
  if (name) params.name = name;
  if (dateFrom) params.dateFrom = dateFrom;
  if (dateTo) params.dateTo = dateTo;
  const response = await apiClient.get<ApiResponse<Manifest[]>>('/manifests', { params: Object.keys(params).length ? params : undefined });
  return response.data;
}

export async function getManifest(id: number): Promise<ApiResponse<ManifestDetail>> {
  const response = await apiClient.get<ApiResponse<ManifestDetail>>(`/manifests/${id}`);
  return response.data;
}

export async function createManifest(data: CreateManifestRequest): Promise<ApiResponse<Manifest>> {
  const response = await apiClient.post<ApiResponse<Manifest>>('/manifests', data);
  return response.data;
}

export async function updateManifest(
  id: number,
  data: UpdateManifestRequest
): Promise<ApiResponse<Manifest>> {
  const response = await apiClient.put<ApiResponse<Manifest>>(`/manifests/${id}`, data);
  return response.data;
}

export async function deleteManifest(id: number): Promise<ApiResponse<null>> {
  const response = await apiClient.delete<ApiResponse<null>>(`/manifests/${id}`);
  return response.data;
}

export async function restoreManifest(id: number): Promise<ApiResponse<null>> {
  const response = await apiClient.post<ApiResponse<null>>(`/manifests/${id}/restore`);
  return response.data;
}

// Barcode Management

export async function importBarcodes(
  manifestId: number,
  data: ImportBarcodesRequest
): Promise<ApiResponse<BarcodeImportResult>> {
  const response = await apiClient.post<ApiResponse<BarcodeImportResult>>(
    `/manifests/${manifestId}/barcodes/bulk`,
    data
  );
  return response.data;
}

export async function addBarcode(
  manifestId: number,
  data: AddBarcodeRequest
): Promise<ApiResponse<ManifestBarcode>> {
  const response = await apiClient.post<ApiResponse<ManifestBarcode>>(
    `/manifests/${manifestId}/barcodes`,
    data
  );
  return response.data;
}

export async function deleteBarcode(
  manifestId: number,
  barcodeId: number
): Promise<ApiResponse<null>> {
  const response = await apiClient.delete<ApiResponse<null>>(
    `/manifests/${manifestId}/barcodes/${barcodeId}`
  );
  return response.data;
}

// Scan Events

export async function getScans(manifestId: number): Promise<ApiResponse<ScanEvent[]>> {
  const response = await apiClient.get<ApiResponse<ScanEvent[]>>(
    `/manifests/${manifestId}/scans`
  );
  return response.data;
}

export async function getScansByBarcodeValue(
  manifestId: number,
  barcodeValue: string
): Promise<ApiResponse<ScanEvent[]>> {
  const response = await apiClient.get<ApiResponse<ScanEvent[]>>(
    `/manifests/${manifestId}/barcodes/${encodeURIComponent(barcodeValue)}/scans`
  );
  return response.data;
}

export async function deleteScan(
  manifestId: number,
  scanId: number
): Promise<ApiResponse<null>> {
  const response = await apiClient.delete<ApiResponse<null>>(
    `/manifests/${manifestId}/scans/${scanId}`
  );
  return response.data;
}

export async function recordScan(
  manifestId: number,
  data: RecordScanRequest
): Promise<ApiResponse<ScanEvent>> {
  const response = await apiClient.post<ApiResponse<ScanEvent>>(
    `/manifests/${manifestId}/scans`,
    data
  );
  return response.data;
}

// Reporting

export async function getReport(manifestId: number): Promise<ApiResponse<ReconciliationReport>> {
  const response = await apiClient.get<ApiResponse<ReconciliationReport>>(
    `/manifests/${manifestId}/report`
  );
  return response.data;
}

export async function getDeletedBarcodes(manifestId: number): Promise<ApiResponse<ManifestBarcode[]>> {
  const response = await apiClient.get<ApiResponse<ManifestBarcode[]>>(
    `/manifests/${manifestId}/deleted-barcodes`
  );
  return response.data;
}

export async function restoreBarcode(manifestId: number, barcodeId: number): Promise<ApiResponse<null>> {
  const response = await apiClient.post<ApiResponse<null>>(`/manifests/${manifestId}/barcodes/${barcodeId}/restore`);
  return response.data;
}

// Per-barcode flag / unflag

export async function flagBarcode(manifestId: number, barcodeId: number): Promise<ApiResponse<null>> {
  const response = await apiClient.post<ApiResponse<null>>(`/manifests/${manifestId}/barcodes/${barcodeId}/flag`);
  return response.data;
}

export async function unflagBarcode(manifestId: number, barcodeId: number): Promise<ApiResponse<null>> {
  const response = await apiClient.post<ApiResponse<null>>(`/manifests/${manifestId}/barcodes/${barcodeId}/unflag`);
  return response.data;
}

/** Admin receive without a barge scan: marks the barcodes scanned so they enter Needs a Home. */
export async function markBarcodesScanned(manifestId: number, barcodeIds: number[]): Promise<ApiResponse<MarkScannedResult>> {
  const response = await apiClient.post<ApiResponse<MarkScannedResult>>(`/manifests/${manifestId}/barcodes/mark-scanned`, { barcodeIds });
  return response.data;
}

// Flagged approval

export async function approveManifest(manifestId: number): Promise<ApiResponse<Manifest>> {
  const response = await apiClient.post<ApiResponse<Manifest>>(`/manifests/${manifestId}/approve`);
  return response.data;
}

export async function rejectManifest(manifestId: number, notes?: string): Promise<ApiResponse<Manifest>> {
  const response = await apiClient.post<ApiResponse<Manifest>>(`/manifests/${manifestId}/reject`, { notes });
  return response.data;
}

// Audit Logs

export async function getAuditLogs(manifestId: number): Promise<ApiResponse<AuditLog[]>> {
  const response = await apiClient.get<ApiResponse<AuditLog[]>>(
    `/manifests/${manifestId}/audit`
  );
  return response.data;
}
